<template>
  <div v-if="editor">
    <div class="bg-gray-50 rounded-tl-md rounded-tr-md p-3">
      <editor-content :editor="editor" />
    </div>
    <input v-if="fieldName" type="hidden" :name="fieldName" :value="editor && editor.getHTML()">
  </div>
  <div v-if="editor" class="mb-2 w-full"
       :class='{
         "border-t-2": true,
         "border-purple-600": editor.isFocused,
         "border-gray-200": !editor.isFocused
       }'>
  </div>
  <div v-if="editor" class="flex justify-end">
    <div class="flex space-x-2">

      <div class="flex items-center space-x-1">
        <svg
          height="20"
          width="20"
          viewBox="0 0 20 20"
          v-if='characterCount > 0 && characterCount <= characterLimit'>
          <circle
            r="10"
            cx="10"
            cy="10"
            class="text-purple-100 fill-current"
          />
          <circle
            r="5"
            cx="10"
            cy="10"
            fill="transparent"
            class="stroke-current"
            :class='{
              "text-purple-400": characterCount < characterLimit,
              "text-green-600": characterCount === characterLimit
            }'
            stroke-width="10"
            :stroke-dasharray="`calc(${percentage} * 31.4 / 100) 31.4`"
            transform="rotate(-90) translate(-20)"
          />
          <circle
            r="6"
            cx="10"
            cy="10"
            class="text-white fill-current"
          />
        </svg>

        <div v-if="characterCount > characterLimit" class="text-sm text-red-700 dark:text-red-400">
          {{ characterLimit - characterCount }}
        </div>
      </div>

      <button v-if="editor.isFocused || characterCount > 0" @click.prevent="submitText" :disabled="characterCount === 0"
              class="border border-gray-200 rounded-full py-2 px-4 inline-flex justify-center text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
              :class='{
                "hidden": characterCount === 0,
                "bg-purple-600 hover:bg-purple-700": characterCount > 0
              }'>
        Post
      </button>
    </div>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import tippy from 'tippy.js'
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Mention from '@tiptap/extension-mention'
import MentionList from './MentionList.vue'

export default {
  components: {
    EditorContent,
  },

  props: {
    placeholder: String,
    fieldName: String,
  },

  emits: ['submit'],

  data() {
    return {
      characterLimit: 2000
    }
  },

  setup(props, context) {
    const editor = ref()

    onMounted(() => {
      editor.value = new Editor({
        editorProps: {
          attributes: {
            class: "text-black break-words whitespace-pre-wrap focus:outline-none"
          }
        },
        extensions: [
          StarterKit.configure({
            blockquote: false, bold: false, bulletList: false, code: false,
            codeBlock: false, dropcursor: false, hardBreak: false, history: false,
            horizontalRule: false, italic: false, listItem: false, orderedList: false,
            strike: false
          }),
          Placeholder.configure({
            emptyEditorClass: 'is-editor-empty text-gray-400',
            placeholder: props.placeholder,
          }),
          Mention.configure({
            HTMLAttributes: { class: 'text-purple-600' },
            suggestion: {
              items: async (query) => {
                const response = await fetch(`/users?q=${query}`, {
                  headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                })
                if (response.ok) {
                  const result = await response.json()
                  return result.map(user => ({ image_url: user.avatar_url, label: `@${user.user_name}` }))
                } else {
                  console.error('Something went wrong', response)
                }
              },
              render() {
                let component
                let popup

                return {
                  onStart: (props) => {
                    component = new VueRenderer(MentionList, {
                      props,
                      editor: props.editor,
                    })

                    popup = tippy('body', {
                      getReferenceClientRect: props.clientRect,
                      appendTo: () => document.body,
                      content: component.element,
                      showOnCreate: true,
                      interactive: true,
                      trigger: 'manual',
                      placement: 'bottom-start',
                    })
                  },
                  onUpdate(props) {
                    component.updateProps(props)

                    popup[0].setProps({
                      getReferenceClientRect: props.clientRect,
                    })
                  },
                  onKeyDown(props) {
                    return component.ref?.onKeyDown(props)
                  },
                  onExit() {
                    popup[0].destroy()
                    component.destroy()
                  },
                }
              }
            }
          })
        ],
      })
    })

    onBeforeUnmount(() => {
      editor.value?.destroy()
    })

    const submitText = (event) => {
      event.stopPropagation()

      if (editor.value.getCharacterCount() > 0) {
        const editorContent = editor.value.getHTML()
        editor.value.commands.clearContent()
        editor.value.commands.blur()
        context.emit('submit', editorContent)
      }
    }

    return { editor, submitText }
  },

  computed: {
    percentage() {
      const charCount = this.characterCount
      const minCharCount = this.characterLimit * 0.05
      const visualCharCount = charCount === 0 ? 0 : Math.max(charCount, minCharCount)
      return Math.round((100 / this.characterLimit) * visualCharCount)
    },
    characterCount() {
      return this.editor.getCharacterCount()
    }
  },
}
</script>

<style>

/* Placeholder (at the top) */
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  pointer-events: none;
  height: 0;
}
</style>